import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, Plus, Target, Calendar } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';
import './WaterTrackingPage.css';

const todayIso = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const currentMonth = () => todayIso().slice(0, 7);

const WaterTrackingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'es' ? 'es-ES' : 'en-US';

  const [target, setTarget] = useState(null);
  const [targetInput, setTargetInput] = useState('');
  const [entries, setEntries] = useState([]);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTarget, setSavingTarget] = useState(false);

  const today = todayIso();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, entriesRes] = await Promise.all([
        apiFetch('/api/water/settings'),
        apiFetch(`/api/water/entries?month=${currentMonth()}`),
      ]);
      if (settingsRes.ok) {
        const s = await settingsRes.json();
        setTarget(s.daily_target_liters);
        setTargetInput(s.daily_target_liters != null ? String(s.daily_target_liters) : '');
      }
      if (entriesRes.ok) {
        setEntries(await entriesRes.json());
      }
    } catch (err) {
      console.error('Error loading water data:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveTarget = async (e) => {
    e.preventDefault();
    const value = parseFloat(targetInput);
    if (!(value > 0)) return;
    setSavingTarget(true);
    try {
      const res = await apiFetch('/api/water/settings', {
        method: 'POST',
        body: JSON.stringify({ daily_target_liters: value }),
      });
      if (res.ok) {
        const data = await res.json();
        setTarget(data.daily_target_liters);
      }
    } finally {
      setSavingTarget(false);
    }
  };

  const addAmount = async (litersFloat) => {
    if (!(litersFloat > 0)) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/water/entries', {
        method: 'POST',
        body: JSON.stringify({ amount_liters: litersFloat }),
      });
      if (res.ok) {
        const entry = await res.json();
        setEntries((prev) => [...prev, entry]);
      }
    } finally {
      setSaving(false);
    }
  };

  const submitCustom = async (e) => {
    e.preventDefault();
    const val = parseFloat(customAmount);
    if (!(val > 0)) return;
    await addAmount(val);
    setCustomAmount('');
  };

  const totalsByDate = useMemo(() => {
    const map = new Map();
    for (const entry of entries) {
      map.set(entry.date, (map.get(entry.date) || 0) + (entry.amount_liters || 0));
    }
    return map;
  }, [entries]);

  const consumedToday = totalsByDate.get(today) || 0;
  const targetOrZero = target || 0;
  const remaining = Math.max(0, targetOrZero - consumedToday);
  const percent = targetOrZero > 0 ? Math.min(100, (consumedToday / targetOrZero) * 100) : 0;

  const history = useMemo(() => {
    const items = Array.from(totalsByDate.entries())
      .filter(([d]) => d !== today)
      .sort((a, b) => (a[0] < b[0] ? 1 : -1));
    return items.slice(0, 30);
  }, [totalsByDate, today]);

  const formatDay = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', weekday: 'short' });
  };

  if (loading) {
    return <div className="loading"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="page-container animate-fade-in" data-testid="water-page">
      <div className="header">
        <div>
          <button className="btn btn-sm btn-secondary mb-2" onClick={() => navigate('/dashboard')}>
            {t('common.back')}
          </button>
          <h1 className="header-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Droplet size={24} color="var(--primary)" />
            {t('water.page_title')}
          </h1>
        </div>
      </div>

      {/* ── Target ──────────────────────────────────────────── */}
      <div className="water-section">
        <div className="water-section-header">
          <Target size={16} />
          <h2>{t('water.target_title')}</h2>
        </div>
        <form onSubmit={saveTarget} className="water-target-form">
          <input
            type="number"
            className="input"
            step="0.1"
            min="0.1"
            max="20"
            placeholder={t('water.target_placeholder')}
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            data-testid="water-target-input"
          />
          <span className="water-unit">L / día</span>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={savingTarget}
            data-testid="water-save-target-btn"
          >
            {savingTarget ? t('water.saving') : t('water.save')}
          </button>
        </form>
      </div>

      {/* ── Today's consumption ─────────────────────────────── */}
      <div className="water-today-card">
        <div className="water-today-header">
          <span className="water-today-label">{t('water.today_title')}</span>
          {target != null && (
            <span className="water-today-target">
              {t('water.target_label')}: {targetOrZero}L
            </span>
          )}
        </div>
        <div className="water-today-value">
          <span className="water-today-consumed">{consumedToday.toFixed(2)}</span>
          <span className="water-today-unit">L</span>
        </div>
        {target != null && targetOrZero > 0 && (
          <>
            <div className="water-progress-bar" aria-label={`${Math.round(percent)}%`}>
              <div className="water-progress-fill" style={{ width: `${percent}%` }} />
            </div>
            <p className="water-remaining">
              {remaining > 0
                ? t('water.remaining', { liters: remaining.toFixed(2) })
                : t('water.goal_reached')}
            </p>
          </>
        )}
      </div>

      {/* ── Quick add buttons ───────────────────────────────── */}
      <div className="water-quick-actions">
        <button
          className="water-quick-btn"
          onClick={() => addAmount(0.25)}
          disabled={saving}
          data-testid="water-add-250"
        >
          <Plus size={14} /> 250 ml
        </button>
        <button
          className="water-quick-btn"
          onClick={() => addAmount(0.5)}
          disabled={saving}
          data-testid="water-add-500"
        >
          <Plus size={14} /> 500 ml
        </button>
        <button
          className="water-quick-btn"
          onClick={() => addAmount(1)}
          disabled={saving}
          data-testid="water-add-1000"
        >
          <Plus size={14} /> 1 L
        </button>
      </div>

      <form onSubmit={submitCustom} className="water-custom-form">
        <input
          type="number"
          className="input"
          step="0.05"
          min="0.05"
          max="10"
          placeholder={t('water.custom_placeholder')}
          value={customAmount}
          onChange={(e) => setCustomAmount(e.target.value)}
          data-testid="water-custom-input"
        />
        <span className="water-unit">L</span>
        <button
          type="submit"
          className="btn btn-secondary"
          disabled={saving}
          data-testid="water-custom-add-btn"
        >
          {t('water.add')}
        </button>
      </form>

      {/* ── History ─────────────────────────────────────────── */}
      <div className="water-section" style={{ marginTop: 20 }}>
        <div className="water-section-header">
          <Calendar size={16} />
          <h2>{t('water.history_title')}</h2>
        </div>
        {history.length === 0 ? (
          <p className="water-empty">{t('water.history_empty')}</p>
        ) : (
          <div className="water-history-list">
            {history.map(([date, total]) => {
              const pct = targetOrZero > 0 ? Math.min(100, (total / targetOrZero) * 100) : 0;
              const reached = targetOrZero > 0 && total >= targetOrZero;
              return (
                <div key={date} className={`water-history-item ${reached ? 'water-reached' : ''}`}>
                  <div className="water-history-info">
                    <span className="water-history-date">{formatDay(date)}</span>
                    <span className="water-history-total">{total.toFixed(2)}L</span>
                  </div>
                  <div className="water-history-bar">
                    <div className="water-history-fill" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WaterTrackingPage;
